import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, TrendingUp } from 'lucide-react';
import { AuthContext, AppSettingsContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TermsOfUse = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount
    fetchTermsOfUse();
  }, []);

  const fetchTermsOfUse = async () => {
    try {
      const response = await axios.get(`${API}/general-settings/public`);
      const settings = response.data;
      setContent(settings.terms_of_use || getDefaultTermsOfUse(settings));
    } catch (error) {
      console.error('Error fetching terms of use:', error);
      setContent(getDefaultTermsOfUse({}));
    } finally {
      setLoading(false);
    }
  };

  const getDefaultTermsOfUse = (settings) => {
    const contactEmail = settings?.contact_email || appSettings?.contact_email || 'support@oddschamp.com';
    const companyName = settings?.company_name || settings?.app_name || appSettings?.company_name || appSettings?.app_name || 'OddsChamp';
    
    return `
      <h2>Terms of Use</h2>
      <p><strong>Last Updated:</strong> ${new Date().toLocaleDateString()}</p>
      
      <h3>1. Acceptance of Terms</h3>
      <p>By accessing and using ${companyName}, you accept and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree to these terms, please do not use our service.</p>

      <h3>2. Description of Service</h3>
      <p>${companyName} is a comprehensive betting tips and analytics platform that provides:</p>
      <ul>
        <li><strong>Betting Tracking:</strong> Track your bets, stakes, odds, outcomes, and performance across multiple bookmakers and leagues</li>
        <li><strong>Analytics & Reports:</strong> Detailed statistics, charts, and insights about your betting performance</li>
        <li><strong>Tipster Services:</strong> Access premium betting tips from verified tipsters, subscribe to their services, and rate their performance</li>
        <li><strong>AI Predictions:</strong> AI-powered betting predictions and analysis for various sports events</li>
        <li><strong>Betting Strategies:</strong> Create, follow, and analyze betting strategies</li>
        <li><strong>Tipster Marketplace:</strong> For tipsters to create services, sell tips, and earn revenue</li>
      </ul>

      <h3>3. Eligibility</h3>
      <p>You must be at least 18 years old to use our services. By using our platform, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these terms.</p>

      <h3>4. User Accounts</h3>
      <p>To access certain features, you must create an account. You are responsible for:</p>
      <ul>
        <li>Providing accurate and complete registration information</li>
        <li>Maintaining the confidentiality of your account credentials</li>
        <li>All activities that occur under your account</li>
        <li>Notifying us immediately of any unauthorized access or security breach</li>
        <li>Keeping your account information up to date</li>
      </ul>
      <p>You may not share your account, use another person's account, or create multiple accounts.</p>

      <h3>5. Acceptable Use Policy</h3>
      <p>You agree not to:</p>
      <ul>
        <li>Use the service for any illegal purpose or in violation of any laws</li>
        <li>Provide false or misleading information</li>
        <li>Infringe on intellectual property rights of others</li>
        <li>Upload malicious code, viruses, or harmful content</li>
        <li>Harass, abuse, threaten, or harm other users</li>
        <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
        <li>Scrape, copy, or reverse engineer any part of the service</li>
        <li>Manipulate or falsify betting records, statistics, or tipster performance data</li>
        <li>Post spam, fraudulent tips, or misleading information</li>
        <li>Use automated systems (bots) without authorization</li>
      </ul>

      <h3>6. Subscriptions and Payments</h3>
      <p>Our platform offers various subscription plans and paid features:</p>
      <ul>
        <li><strong>Premium Subscription:</strong> Access to advanced features, detailed analytics, and AI predictions</li>
        <li><strong>Tip Purchases:</strong> One-time purchases of individual betting tips from tipsters</li>
        <li><strong>Service Subscriptions:</strong> Recurring subscriptions to tipster services</li>
      </ul>
      <p>By subscribing or purchasing, you agree to:</p>
      <ul>
        <li>Pay all fees associated with your chosen plan or purchase</li>
        <li>Recurring charges for subscription renewals (unless cancelled)</li>
        <li>Payments are processed through Stripe and PayPal</li>
        <li>All sales are final; refunds are provided at our sole discretion</li>
        <li>Subscription fees are non-refundable except as required by law</li>
      </ul>

      <h3>7. Tipster Services and Earnings</h3>
      <p>If you provide tipster services on our platform:</p>
      <ul>
        <li>You must provide accurate information about your tips and performance</li>
        <li>You are responsible for the quality and accuracy of your tips</li>
        <li>Platform fees will be deducted from your earnings as specified in your tipster agreement</li>
        <li>Withdrawals are subject to minimum thresholds and verification</li>
        <li>You must comply with all applicable tax laws and regulations</li>
        <li>We reserve the right to suspend or terminate your tipster account for violations or poor performance</li>
      </ul>

      <h3>8. Intellectual Property Rights</h3>
      <p>All content, features, functionality, trademarks, logos, and designs are owned by ${companyName} and are protected by copyright, trademark, and other intellectual property laws.</p>
      <p>You may not:</p>
      <ul>
        <li>Copy, modify, distribute, or create derivative works</li>
        <li>Use our trademarks or branding without permission</li>
        <li>Remove copyright or proprietary notices</li>
      </ul>

      <h3>9. User-Generated Content</h3>
      <p>You retain ownership of content you submit (bets, tips, reviews, comments). By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to:</p>
      <ul>
        <li>Use, display, reproduce, and distribute your content as part of our service</li>
        <li>Create derivative works for service improvement</li>
        <li>Sublicense to other users (e.g., tips you sell)</li>
      </ul>
      <p>You represent and warrant that:</p>
      <ul>
        <li>You own or have rights to all content you submit</li>
        <li>Your content does not violate any laws or rights of others</li>
        <li>Your content is accurate and not misleading</li>
      </ul>

      <h3>10. No Investment or Gambling Advice</h3>
      <p><strong>Important Disclaimer:</strong></p>
      <ul>
        <li>${companyName} is a tracking, analytics, and information platform only</li>
        <li>We do not provide investment, financial, or gambling advice</li>
        <li>Tips and predictions are for informational purposes only</li>
        <li>Past performance does not guarantee future results</li>
        <li>All betting carries risk; you may lose money</li>
        <li>You are solely responsible for your betting decisions</li>
        <li>We do not promote, facilitate, or endorse gambling</li>
      </ul>

      <h3>11. Legal Compliance and Responsible Gambling</h3>
      <p>You are responsible for:</p>
      <ul>
        <li>Complying with all gambling laws and regulations in your jurisdiction</li>
        <li>Ensuring betting is legal in your location</li>
        <li>Understanding the risks associated with gambling</li>
        <li>Gambling responsibly and within your means</li>
        <li>Seeking help if you have a gambling problem</li>
      </ul>
      <p>If gambling is illegal in your jurisdiction, you must not use our service for betting-related activities.</p>

      <h3>12. Disclaimer of Warranties</h3>
      <p>The service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied. We do not guarantee:</p>
      <ul>
        <li>Uninterrupted, secure, or error-free operation</li>
        <li>Accuracy, reliability, or completeness of information, tips, or predictions</li>
        <li>Fitness for a particular purpose or merchantability</li>
        <li>That defects will be corrected</li>
        <li>That the service is free from viruses or harmful components</li>
        <li>Results or outcomes from using tips or predictions</li>
      </ul>

      <h3>13. Limitation of Liability</h3>
      <p>To the maximum extent permitted by law, ${companyName} and its affiliates, officers, directors, employees, and agents shall not be liable for:</p>
      <ul>
        <li>Any indirect, incidental, special, consequential, or punitive damages</li>
        <li>Loss of profits, revenue, data, or use</li>
        <li>Betting losses or financial losses</li>
        <li>Damages resulting from unauthorized access to your account</li>
        <li>Errors or inaccuracies in content, tips, or predictions</li>
        <li>Actions or inactions of third parties (bookmakers, payment processors, etc.)</li>
      </ul>
      <p>Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim.</p>

      <h3>14. Indemnification</h3>
      <p>You agree to indemnify, defend, and hold harmless ${companyName} from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:</p>
      <ul>
        <li>Your use or misuse of the service</li>
        <li>Your violation of these Terms of Use</li>
        <li>Your violation of any laws or rights of third parties</li>
        <li>Your content or activities on the platform</li>
      </ul>

      <h3>15. Termination and Suspension</h3>
      <p>We reserve the right to, at any time and without prior notice:</p>
      <ul>
        <li>Suspend or terminate your account for violation of these terms</li>
        <li>Refuse service to anyone for any reason</li>
        <li>Remove or disable content that violates these terms</li>
        <li>Discontinue or modify the service</li>
      </ul>
      <p>You may terminate your account at any time through your account settings. Upon termination:</p>
      <ul>
        <li>Your access to paid features will cease immediately</li>
        <li>No refunds will be provided for remaining subscription periods</li>
        <li>Your data will be retained according to our Privacy Policy</li>
      </ul>

      <h3>16. Changes to Service and Terms</h3>
      <p>We reserve the right to:</p>
      <ul>
        <li>Modify, suspend, or discontinue any part of the service at any time</li>
        <li>Change subscription prices with notice to existing subscribers</li>
        <li>Update these Terms of Use at any time</li>
      </ul>
      <p>We will notify you of material changes via email or through a prominent notice on our platform. Continued use after changes constitutes acceptance of the new terms.</p>

      <h3>17. Third-Party Services and Links</h3>
      <p>Our platform may contain links to third-party websites or integrate with third-party services (Stripe, PayPal, etc.). We are not responsible for:</p>
      <ul>
        <li>The content, privacy practices, or terms of third-party services</li>
        <li>Any damages or losses caused by third-party services</li>
      </ul>

      <h3>18. Dispute Resolution</h3>
      <p>Any disputes arising from these terms or your use of the service shall be resolved through:</p>
      <ul>
        <li>Good faith negotiation between the parties</li>
        <li>Mediation or arbitration as mutually agreed</li>
        <li>Applicable courts in our jurisdiction if negotiation fails</li>
      </ul>

      <h3>19. Governing Law and Jurisdiction</h3>
      <p>These terms shall be governed by and construed in accordance with the laws of the jurisdiction where ${companyName} operates, without regard to conflict of law principles.</p>

      <h3>20. Severability</h3>
      <p>If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</p>

      <h3>21. Entire Agreement</h3>
      <p>These Terms of Use, together with our Privacy Policy and GDPR Compliance statement, constitute the entire agreement between you and ${companyName} regarding the use of our service.</p>

      <h3>22. Contact Information</h3>
      <p>For questions, concerns, or notices regarding these terms, please contact us at: <strong>${contactEmail}</strong></p>
      
      <p style="margin-top: 24px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,0.1);">
        <strong>Related Documents:</strong><br>
        <a href="/privacy-policy" style="color: #60a5fa; text-decoration: underline;">Privacy Policy</a> | 
        <a href="/gdpr" style="color: #60a5fa; text-decoration: underline;">GDPR Compliance</a>
      </p>
    `;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 flex items-center justify-center">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </Layout>
    );
  }

  const pageContent = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <Card className="glass-dark border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center text-2xl">
              <FileText className="w-6 h-6 mr-2 text-blue-400" />
              Terms of Use
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: content }}
              style={{
                color: '#e5e7eb',
                fontSize: '0.95rem',
                lineHeight: '1.7'
              }}
            />
            
            <div className="mt-8 pt-6 border-t border-white/10">
              {user ? (
                <Link 
                  to="/dashboard"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  ← Back to Dashboard
                </Link>
              ) : (
                <Link 
                  to="/"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  ← Back to Home
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // If user is logged in, use Layout, otherwise use public navigation
  if (user) {
    return <Layout>{pageContent}</Layout>;
  }

  // Public view with Landing page-style navigation
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Public Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              {appSettings.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings.app_name} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold whitespace-nowrap">{appSettings.app_name}</span>
                </>
              )}
            </div>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Content with top padding for fixed nav */}
      <div className="pt-16">
        {pageContent}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsOfUse;
